import { Matrix, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { DeleteElements, InsertElements } from "../../core/cmd";
import { ElementChangedFlags } from "../../core/mdl";
import { Log } from "../../utils";
import { clone } from "../../utils/array";
import { ERR_FAILED, ERR_INVALID_STATE, newError, rejectError } from "../../utils/error";
import * as basket from "./../../eaiws/basket";
import { Helpers } from "../Helpers";
import { ArticlePropertyHelpers } from "../ArticlePropertyHelpers";
/** @internal */
export async function executeActions(pManager, pArticle, pActionIds, pInteractor, pActionChoiceStack) {
    if (pArticle == null || pActionIds == null || pActionIds.length === 0)
        return;
    try {
        beginExecution(pManager);
        await tryExecuteActionsOnServer(pManager, pArticle, pActionIds, pInteractor, pActionChoiceStack);
    }
    catch (pError) {
        pManager.eventOapError.trigger({
            /* eslint-disable-next-line @typescript-eslint/no-unsafe-argument */
            error: (pError instanceof Error) ? pError : new Error(pError),
            article: pArticle,
            interactor: pInteractor.oapInteractor
        });
        Log.error("Failed to execute oap actions", pError);
    }
    finally {
        endExecution(pManager);
    }
}
function beginExecution(pManager) {
    if (pManager.app.appCallbacks.beginAsyncOperation != null)
        pManager.app.appCallbacks.beginAsyncOperation();
    pManager.app.model.beginModification();
    pManager.app.model.beginSelectionChange();
}
function endExecution(pManager) {
    if (pManager.app.appCallbacks.endAsyncOperation != null)
        pManager.app.appCallbacks.endAsyncOperation();
    pManager.app.model.endSelectionChange();
    pManager.app.model.endModification();
}
async function tryExecuteActionsOnServer(pManager, pArticle, pActionIds, pInteractor, pActionChoiceStack) {
    if (pArticle == null || pActionIds == null || pActionIds.length === 0)
        return;
    await pArticle.ensureIsInstanced();
    if (pArticle.basketId == null)
        return (rejectError(ERR_INVALID_STATE, "basketId is null"));
    //try to execute on server
    const tContext = new basket.OAPActionContext();
    tContext.self = pArticle.basketId;
    tContext.dpr = window.devicePixelRatio >= 1.5 ? 2 : 1; //define device pixel ratio
    if (pInteractor != null)
        tContext.interactor = pInteractor.oapInteractor.id;
    const tResults = await pManager.session.basket.oapProcessActions(pActionIds, true, tContext);
    if (tResults == null || tResults.length === 0)
        throw new Error("Failed to execute oap actions.");
    //ppocess action results
    let tUpdateArticle = false;
    let tClientSideActionIdx = null;
    const tCreateObjectActions = new Array();
    for (let i = 0; i < tResults.length; ++i) {
        const tResult = tResults[i];
        if (tResult.state === "Failure")
            throw new Error("Action " + tResult.id + " failed");
        if (tResult.state === "NotResponsible") {
            tClientSideActionIdx = i;
            break;
        }
        else {
            if (isCreateObjectAttachAreaAction(tResult)) {
                tCreateObjectActions.push(tResult);
            }
            else {
                if (!tUpdateArticle) {
                    tUpdateArticle =
                        (tResult.addedItems != null && tResult.addedItems.length > 0) ||
                            (tResult.movedItems != null && tResult.movedItems.length > 0) ||
                            (tResult.removedItems != null && tResult.removedItems.length > 0) ||
                            (tResult.updatedItems != null && tResult.updatedItems.length > 0);
                }
            }
        }
    }
    //get remaining action ids
    let tRemainingActionIds = null;
    if (tClientSideActionIdx != null)
        tRemainingActionIds = pActionIds.slice(tClientSideActionIdx + 1);
    //create articles created by create object actions
    const tNeedUpdate = await insertCreateObjectArticles(pManager, tCreateObjectActions);
    if (tNeedUpdate)
        tUpdateArticle = true;
    //update main article
    if (tUpdateArticle) {
        const tMainArticle = pArticle.getMainArticle();
        //TODO: maybe update only changed items?
        tMainArticle._clearCachedArticleInfo(tMainArticle, true);
        await tMainArticle.updateGeometry();
        pManager._notifyArticleChanged(tMainArticle, true);
        //properties may have changed -> trigger event
        if (pArticle.isInScene())
            pArticle.notifyChange(ElementChangedFlags.Properties);
    }
    //execute client side action
    let tContinueExecution = true;
    if (tClientSideActionIdx != null) {
        const tAction = tResults[tClientSideActionIdx];
        tContinueExecution = await executeActionOnClient(pManager, pArticle, tAction, pInteractor, tRemainingActionIds, pActionChoiceStack);
    }
    //execute remaining actions on server
    if (tContinueExecution && tRemainingActionIds != null && tRemainingActionIds.length > 0)
        await tryExecuteActionsOnServer(pManager, pArticle, tRemainingActionIds, pInteractor);
}
//returns true if execution of remaining actions should be continued
async function executeActionOnClient(pManager, pArticle, pOapActionResult, pInteractor, pSubsequentActions, pActionChoiceStack) {
    if (pOapActionResult.actionData == null)
        return (false);
    const tAction = pOapActionResult.actionData;
    if (tAction instanceof basket.OAPActionChoiceAction) {
        executeActionChoiceAction(pManager, pArticle, tAction, pInteractor, pSubsequentActions, pOapActionResult.objects, pActionChoiceStack);
        return (false); //done later after action choice was executed
    }
    if (tAction instanceof basket.OAPPropEditAction) {
        await executePropEditAction(pManager, pArticle, tAction, pInteractor, pSubsequentActions, pOapActionResult.objects);
        return (false); //done later after property was edited
    }
    if (tAction instanceof basket.OAPPropEdit2Action) {
        await executePropEdit2Action(pManager, pArticle, tAction, pInteractor, pSubsequentActions, pOapActionResult.objects);
        return (false); //done later after property was edited
    }
    if (tAction instanceof basket.OAPCreateObjectAction) {
        await executeCreateObjectAction(pManager, pArticle, tAction);
        return (true);
    }
    if (tAction instanceof basket.OAPDeleteObjectAction) {
        await executeDeleteObjectAction(pManager, pArticle, tAction, pOapActionResult.objects);
        return (true);
    }
    if (tAction instanceof basket.OAPSelectObjectAction) {
        await executeSelectObjectAction(pManager, pArticle, tAction, pOapActionResult.objects);
        return (false); //According to OAP spec execution has to be aborted
    }
    if (tAction instanceof basket.OAPShowMediaAction) {
        executeShowMediaAction(pManager, tAction, pInteractor);
        return (false); //According to OAP spec execution has to be aborted
    }
    if (tAction instanceof basket.OAPMessageAction) {
        executeMessageAction(pManager, tAction, pInteractor);
        return (false); //According to OAP spec execution has to be aborted
    }
    Log.warn("OAPAction not implemented: " + JSON.stringify(tAction));
    return (false);
}
function executeActionChoiceAction(pManager, pArticle, pOapAction, pInteractor, pSubsequentActions, pResolvedObjectIds, pActionChoiceStack) {
    if (pOapAction.actions.length === 0)
        return;
    //create action choice stack to enable the user to go backwards
    let tGoBackFunc;
    let tNextStack;
    if (pActionChoiceStack != null && pActionChoiceStack.length > 0) {
        tNextStack = clone(pActionChoiceStack);
        tGoBackFunc = () => {
            const tPrevStack = clone(pActionChoiceStack);
            const tStackEntry = tPrevStack.pop();
            executeActionChoiceAction(pManager, pArticle, tStackEntry.oapAction, pInteractor, tStackEntry.subsequentActions, pResolvedObjectIds, tPrevStack);
        };
    }
    else {
        tNextStack = [];
    }
    tNextStack.push({
        oapAction: pOapAction,
        subsequentActions: pSubsequentActions
    });
    //build array of choice actions
    const tActions = new Array();
    for (const tItem of pOapAction.actions) {
        const tAction = {};
        tAction.text = tItem.text;
        tAction.image = tItem.imageFile;
        tAction.execute = () => {
            let tActionIds;
            if (tItem.actionIds != null)
                tActionIds = tItem.actionIds;
            else if (tItem.action != null) //fallback if OAP client capability `MultiActionChoice` is not set.
                tActionIds = [tItem.action.id];
            else
                tActionIds = [];
            //also execute subsequent actions
            if (pSubsequentActions != null && pSubsequentActions.length > 0)
                tActionIds.push(...pSubsequentActions);
            return (executeActions(pManager, pArticle, tActionIds, pInteractor, tNextStack));
        };
        tActions.push(tAction);
    }
    //Delay the dialog. This is necessary for nested action choice dialogs to work properly.
    //Handling inside the gui would be more compilcated otherwise.
    setTimeout(() => {
        if (pManager.app.appCallbacks.showActionChoice != null) {
            let tTileSize;
            if (pOapAction.tileSize != null) {
                switch (pOapAction.tileSize) {
                    case "Large":
                        tTileSize = "large";
                        break;
                    case "Medium":
                        tTileSize = "medium";
                        break;
                    default:
                        tTileSize = "small";
                }
            }
            pManager.app.appCallbacks.showActionChoice(tActions, {
                title: pOapAction.title,
                displayMode: pOapAction.viewType === "List" ? "list" : "tile",
                tileSize: tTileSize,
                originReference: pInteractor.cachedBoundingClientRect,
                goBack: tGoBackFunc
            });
        }
    });
}
function executePropEditAction(pManager, pArticle, pOapAction, pInteractor, pSubsequentActions, pResolvedObjectIds) {
    //map propEdit to propEdit2
    const tOapAction = new basket.OAPPropEdit2Action();
    tOapAction.id = pOapAction.id;
    tOapAction.objects = pOapAction.objects;
    tOapAction.title = pOapAction.title;
    if (pOapAction.propertyClasses != null) {
        tOapAction.propertyClasses = [];
        for (const tClass of pOapAction.propertyClasses) {
            const tClass2 = new basket.OAPPropEditClass();
            tClass2.propClass = tClass;
            tClass2.editableOnly = pOapAction.editableOnly;
            tClass2.visibleOnly = pOapAction.visibleOnly;
            tOapAction.propertyClasses.push(tClass2);
        }
    }
    if (pOapAction.properties != null) {
        tOapAction.properties = [];
        for (const tProp of pOapAction.properties) {
            const tProp2 = new basket.OAPPropEditProp();
            tProp2.propName = tProp;
            tProp2.editableOnly = pOapAction.editableOnly;
            tProp2.visibleOnly = pOapAction.visibleOnly;
            tOapAction.properties.push(tProp2);
        }
    }
    return executePropEdit2Action(pManager, pArticle, tOapAction, pInteractor, pSubsequentActions, pResolvedObjectIds);
}
async function executePropEdit2Action(pManager, pArticle, pOapAction, pInteractor, pSubsequentActions, pResolvedObjectIds) {
    if (pOapAction.objects == null || pOapAction.objects.length !== 1)
        return;
    const tResolvedObjects = await resolveObjectDefinitions(pManager, pOapAction.objects, pResolvedObjectIds, pArticle);
    if (tResolvedObjects == null || tResolvedObjects.length < 1) {
        Log.warn("Failed to find prop edit target.");
        return;
    }
    const tTarget = tResolvedObjects[0];
    //build property and class map
    const tPropMap = new Map();
    if (pOapAction.properties != null) {
        for (const tProp of pOapAction.properties) {
            tPropMap.set(tProp.propName, tProp);
        }
    }
    const tClassMap = new Map();
    if (pOapAction.propertyClasses != null) {
        for (const tClass of pOapAction.propertyClasses) {
            tClassMap.set(tClass.propClass, tClass);
        }
    }
    const tGetPropsCallback = async () => {
        var _a;
        const tSelectedProps = new Array();
        if (tTarget == null)
            return (tSelectedProps);
        const tProps = await tTarget.getProperties();
        if (tProps == null)
            return (tSelectedProps);
        for (const tProp of tProps) {
            const tEaiwsProp = (_a = ArticlePropertyHelpers.GetPropertyUserData(tProp)) === null || _a === void 0 ? void 0 : _a.eaiwsProperty;
            if (tEaiwsProp != null) {
                //check prop class
                const tOapClass = tClassMap.get(tEaiwsProp.propClass);
                if (tOapClass != null) {
                    //add property and setup visibilty
                    tProp.visible = (tProp.visible || !tOapClass.visibleOnly) && (tProp.editable || !tOapClass.editableOnly);
                    tSelectedProps.push(tProp);
                    continue;
                }
                //check property
                const tOapProp = tPropMap.get(tEaiwsProp.propName);
                if (tOapProp != null) {
                    //add property and setup visibilty
                    tProp.visible = (tProp.visible || !tOapProp.visibleOnly) && (tProp.editable || !tOapProp.editableOnly);
                    tSelectedProps.push(tProp);
                    continue;
                }
            }
        }
        if (tSelectedProps.length === 1) {
            //wrap setValue function to allow execution of subsequent actions
            //only supported for one property
            const tOrgSetValue = tSelectedProps[0].setValue;
            tSelectedProps[0].setValue = async (pValue) => {
                let tResult;
                try {
                    beginExecution(pManager);
                    tResult = await tOrgSetValue(pValue);
                    //execute subsequent actions
                    if (pSubsequentActions != null && pSubsequentActions.length > 0) {
                        void executeActions(pManager, pArticle, pSubsequentActions, pInteractor);
                    }
                }
                finally {
                    endExecution(pManager);
                }
                return (tResult);
            };
            //single properties are always editable and visible
            tSelectedProps[0].visible = true;
            tSelectedProps[0].editable = true;
        }
        return (tSelectedProps);
    };
    //Delay the dialog. This is necessary for nested property dialogs to work properly.
    //Handling inside the gui would be more complicated otherwise.
    setTimeout(() => {
        if (tTarget != null && pManager.app.appCallbacks.showPropertyEditor != null) {
            pManager.app.appCallbacks.showPropertyEditor(tTarget, tGetPropsCallback, {
                title: pOapAction.title,
                originReference: pInteractor.cachedBoundingClientRect
            });
        }
    });
}
//TODO: this is only a test implementation, do we need this on client side at all?
async function executeCreateObjectAction(pManager, pArticle, pOapAction) {
    if (pOapAction.articleSpecMode === "Self") {
        Log.warn("articleSpecMode 'Self' not implemented");
        return;
    }
    if (pOapAction.baseArticleNumber == null) {
        Log.warn("missing baseArticleNumber");
        return;
    }
    const tParent = await resolveObjectDefinition(pOapAction.parent, pArticle);
    const tFatherId = tParent != null ? tParent.basketId : null;
    let tBeforeId = null;
    const tInsertInfo = new basket.InsertInfo();
    tInsertInfo.articlePackageId = pOapAction.packageId;
    tInsertInfo.baseArticleNumber = pOapAction.baseArticleNumber;
    tInsertInfo.ofmlVariantCode = pOapAction.ofmlVariantCode;
    const tOptions = new basket.InsertArticleOptions();
    if (pOapAction.initialPlacement instanceof basket.OAPDataDefinedPlacement) {
        tOptions.attachPoint = pOapAction.initialPlacement.attachPoint;
        tOptions.attachmentMode = tParent != null ? "Composite" : "None";
        const tRefObject = await resolveObjectDefinition(pOapAction.initialPlacement.referenceObject, pArticle);
        if (tRefObject != null)
            tBeforeId = tRefObject.basketId;
    }
    else if (pOapAction.initialPlacement instanceof basket.OAPAttachAreasPlacement) {
        Log.warn("OAPAttachAreasPlacement not implemented");
    }
    const tArticleId = await pManager.session.basket.insertOFMLArticle(tFatherId, tBeforeId, tInsertInfo, tOptions);
    if (tParent == null) {
        //create new article
        const tNewArticle = await pManager.createArticleById(tArticleId);
        await insertArticle(pManager, tNewArticle, null);
    }
    else {
        //update parent (was inserted as child)
        const tMainArticle = tParent.getMainArticle();
        await tMainArticle.updateGeometry();
        tMainArticle.notifyArticleChanged(tMainArticle, true);
    }
}
async function executeDeleteObjectAction(pManager, pArticle, pOapAction, pResolvedObjectIds) {
    const tResolvedObjects = await resolveObjectDefinitions(pManager, pOapAction.objects, pResolvedObjectIds, pArticle);
    if (tResolvedObjects == null || tResolvedObjects.length === 0)
        return;
    const tSubArticles = new Array();
    const tSubArticlesIds = new Array();
    const tMainArticles = new Array();
    for (const pArticle of tResolvedObjects) {
        if (pArticle.isMainArticle())
            tMainArticles.push(pArticle);
        else {
            if (pArticle.basketId != null) {
                tSubArticles.push(pArticle);
                tSubArticlesIds.push(pArticle.basketId);
            }
        }
    }
    //delete sub articles
    if (tSubArticles.length > 0) {
        const tOptions = new basket.DeleteItemsOptions();
        tOptions.subArticles = true;
        await pManager.session.basket.deleteItems(tSubArticlesIds, tOptions);
        const tMainArticle = pArticle.getMainArticle();
        await tMainArticle.updateGeometry();
        tMainArticle.notifyArticleChanged(tMainArticle, true);
    }
    //delete main articles
    if (tMainArticles.length > 0) {
        const tCmd = new DeleteElements(pManager.app, tMainArticles);
        await pManager.app.commands.executeCommand(tCmd);
    }
}
async function executeSelectObjectAction(pManager, pArticle, pOapAction, pResolvedObjectIds) {
    const tResolvedObjects = await resolveObjectDefinitions(pManager, pOapAction.objects, pResolvedObjectIds, pArticle);
    if (tResolvedObjects == null || tResolvedObjects.length === 0)
        return;
    const tTarget = tResolvedObjects[0];
    const tMainArticle = tTarget.getMainArticle();
    let tSubArticle = null;
    if (tMainArticle !== tTarget && tTarget.isSubArticle()) {
        tSubArticle = tTarget;
    }
    if (tMainArticle != null) {
        pManager.app.model.setSelection([tMainArticle]);
        if (tSubArticle != null) {
            pManager.app.model.setSubElementSelection(tMainArticle, tSubArticle);
        }
    }
}
function executeShowMediaAction(pManager, pOapAction, pInteractor) {
    if (pOapAction.mediaSource !== "YouTube") {
        Log.warn("Unsupported mediaSource: " + pOapAction.mediaSource);
        return;
    }
    if (pManager.app.appCallbacks.showMedia != null) {
        pManager.app.appCallbacks.showMedia({
            id: pOapAction.mediaId,
            type: pOapAction.mediaSource
        }, {
            originReference: pInteractor.cachedBoundingClientRect
        });
    }
}
function executeMessageAction(pManager, pOapAction, pInteractor) {
    if (pManager.app.appCallbacks.showMessage != null) {
        pManager.app.appCallbacks.showMessage({
            text: pOapAction.text
        }, {
            originReference: pInteractor.cachedBoundingClientRect
        });
    }
}
async function resolveObjectDefinitions(pManager, pDefinitions, pResolvedObjectIds, pSelf) {
    //EAIWS already resolved the ids
    if (pResolvedObjectIds != null && pResolvedObjectIds.length > 0) {
        const tResults = new Array();
        const tMainArticle = pSelf.getMainArticle();
        for (const tId of pResolvedObjectIds) {
            if (tId === pSelf.basketId) { //id = self?
                tResults.push(pSelf);
            }
            else if (tId === tMainArticle.basketId) { //id = main article ?
                tResults.push(tMainArticle);
            }
            else {
                const tSub = tMainArticle.getSubArticleById(tId); //id = sub article ?
                if (tSub != null) {
                    tResults.push(tSub);
                }
                else {
                    //article is not part of current main article, search whole scene
                    const tArticle = pManager.getArticleById(tId);
                    if (tArticle != null) {
                        tResults.push(tArticle);
                    }
                }
            }
        }
        return (tResults);
    }
    //try to resolve objects ourselfs
    if (pDefinitions != null && pDefinitions.length > 0) {
        const tResults = new Set();
        for (const tObj of pDefinitions) {
            const tArticle = await resolveObjectDefinition(tObj, pSelf);
            if (tArticle != null)
                tResults.add(tArticle);
        }
        return (Array.from(tResults));
    }
    return (null);
}
async function resolveObjectDefinition(pDefinition, pSelf) {
    if (pDefinition == null || pSelf == null)
        return (null);
    let tDef = null;
    switch (pDefinition.category) {
        case "Self":
            tDef = pSelf;
            break;
        case "TopArticle":
            tDef = pSelf.getMainArticle();
            break;
        case "ParentArticle":
            tDef = pSelf.getParentArticle();
            break;
        default:
            Log.warn("OAPObjectDefinition not implemented: " + pDefinition.category);
    }
    if (tDef != null)
        await tDef.ensureIsInstanced();
    return (tDef);
}
function isCreateObjectAttachAreaAction(pActionResult) {
    if (pActionResult.actionData == null || !(pActionResult.actionData instanceof basket.OAPCreateObjectAction))
        return (false);
    if (pActionResult.actionData.parent != null)
        return (false);
    if (pActionResult.referenceObject == null)
        return (false);
    if (pActionResult.addedItems == null || pActionResult.addedItems.length <= 0)
        return (false);
    return (pActionResult.actionData.initialPlacement instanceof basket.OAPAttachAreasPlacement);
}
async function insertCreateObjectArticles(pManager, pResults) {
    let tUpdateRefArticle = false;
    for (const tAction of pResults) {
        pManager.app.commands.beginUndoSection();
        const tNeedUpdate = await insertCreateObjectArticle(pManager, tAction);
        if (tNeedUpdate)
            tUpdateRefArticle = true;
        pManager.app.commands.endUndoSection();
    }
    return (tUpdateRefArticle);
}
async function insertCreateObjectArticle(pManager, pResult) {
    let tNeedRefArticleUpdate = false;
    try {
        if (pResult.addedItems != null && pResult.addedItems.length > 0) {
            const tOptions = new basket.GetAllItemsOptions();
            tOptions.wholeComposite = true;
            tOptions.geometryIds = true;
            tOptions.setArticleIds = true;
            let tMainArticleId;
            const tAllItems = await pManager.session.basket.getAllItems(pResult.addedItems, tOptions);
            //find main article and check if new sub articles were added to the reference object
            for (const tItem of tAllItems) {
                if (tItem.getMainArticleId() === tItem.itemId && tItem.itemId !== pResult.referenceObject) {
                    tMainArticleId = tItem.itemId;
                }
                if (pResult.referenceObject != null && tItem.getMainArticleId() === pResult.referenceObject) {
                    tNeedRefArticleUpdate = true;
                }
            }
            if (tMainArticleId == null) {
                throw newError(ERR_FAILED, "main article not found");
            }
            //insert new article by id
            const tArticle = await pManager.createArticleById(tMainArticleId, false, tAllItems);
            await insertArticle(pManager, tArticle, getArticleAttachAreaMatrix(pManager, pResult));
        }
    }
    catch (pError) {
        Log.error(ERR_FAILED, pError);
    }
    return (tNeedRefArticleUpdate ||
        (pResult.movedItems != null && pResult.movedItems.length > 0) ||
        (pResult.removedItems != null && pResult.removedItems.length > 0) ||
        (pResult.updatedItems != null && pResult.updatedItems.length > 0));
}
function getArticleAttachAreaMatrix(pManager, pResult) {
    if (pResult.referenceObject == null)
        return (null);
    const tRefArticle = pManager.getArticleById(pResult.referenceObject);
    if (tRefArticle == null)
        return (null);
    const tPos = Helpers.Vector3FromEaiwsVector(pResult.newObjectPosition);
    const tRot = Helpers.QuaternionFromEaiwsRotation(pResult.newObjectRotation);
    const tMatrix = Matrix.Compose(new Vector3(1, 1, 1), tRot, tPos);
    tMatrix.multiplyToRef(tRefArticle.worldTransform, tMatrix);
    return (tMatrix);
}
async function insertArticle(pManager, pArticle, pMatrix) {
    const tElements = [pArticle];
    //set transform
    if (pMatrix != null)
        pArticle.setTransform(pMatrix);
    //insert to model
    const tCmd = new InsertElements(pManager.app, tElements);
    await pManager.app.commands.executeCommand(tCmd);
    //select article
    pManager.app.model.setSelection(tElements);
}
